import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, GripVertical, Sparkles, Languages } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const EMPTY = {
  instrument_id: "",
  section: "MENTAL_HEALTH",
  title: "",
  description: "",
  scoring: "sum",
  enabled: false,
  response_scale: [
    { value: 0, label: "No" },
    { value: 1, label: "Yes" },
  ],
  items: [{ item_id: "Q1", text: "", reverse: false }],
  cutoffs: [
    { max: 0, band: "Normal", label: "Below screening threshold" },
    { max: 100, band: "High", label: "Above threshold" },
  ],
  cohort_ids: [],
  site_ids: [],
  translations: {},
};

const LANG_OPTIONS = [
  { code: "hi", label: "Hindi (हिन्दी)" },
  { code: "ta", label: "Tamil (தமிழ்)" },
  { code: "te", label: "Telugu (తెలుగు)" },
  { code: "ml", label: "Malayalam (മലയാളം)" },
  { code: "kn", label: "Kannada (ಕನ್ನಡ)" },
  { code: "bn", label: "Bengali (বাংলা)" },
  { code: "mr", label: "Marathi (मराठी)" },
  { code: "gu", label: "Gujarati (ગુજરાતી)" },
  { code: "pa", label: "Punjabi (ਪੰਜਾਬੀ)" },
];

const SECTIONS = [
  { key: "MENTAL_HEALTH", label: "Mental Health" },
  { key: "BRAIN_HEALTH", label: "Brain Health" },
  { key: "ADDICTIONS", label: "Addictions" },
  { key: "OTHER", label: "Other" },
];

const SCORING_MODES = [
  { key: "sum", label: "Sum of item values" },
  { key: "sum_reverse", label: "Sum with reverse-scored items (max − v)" },
  { key: "sum_inverted", label: "Sum inverted (higher = more concern)" },
];

const BANDS = ["Normal", "Mild", "Moderate", "High"];

export default function InstrumentBuilder({ open, onOpenChange, initial, cohorts, sites, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState("hi");
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY,
          ...initial,
          items: (initial.items || []).map((i) => ({ ...i, reverse: !!i.reverse })),
          response_scale: initial.response_scale || EMPTY.response_scale,
          cutoffs: initial.cutoffs || EMPTY.cutoffs,
          cohort_ids: initial.cohort_ids || [],
          site_ids: initial.site_ids || [],
          translations: initial.translations || {},
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setListItem = (key, idx, patch) =>
    setForm((f) => ({ ...f, [key]: f[key].map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));

  const removeListItem = (key, idx) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [...f.items, { item_id: `Q${f.items.length + 1}`, text: "", reverse: false }],
    }));

  const addScale = () =>
    setForm((f) => ({
      ...f,
      response_scale: [...f.response_scale, { value: f.response_scale.length, label: "" }],
    }));

  const addCutoff = () =>
    setForm((f) => ({
      ...f,
      cutoffs: [...f.cutoffs, { max: 0, band: "Normal", label: "" }],
    }));

  const toggleScope = (key, id) => {
    setForm((f) => {
      const cur = f[key] || [];
      return { ...f, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });
  };

  const setTrField = (lang, field, value) => {
    setForm((f) => ({
      ...f,
      translations: {
        ...(f.translations || {}),
        [lang]: { ...(f.translations?.[lang] || {}), [field]: value },
      },
    }));
  };

  const setTrNested = (lang, key, itemKey, value) => {
    setForm((f) => {
      const cur = f.translations?.[lang] || {};
      return {
        ...f,
        translations: {
          ...(f.translations || {}),
          [lang]: { ...cur, [key]: { ...(cur[key] || {}), [itemKey]: value } },
        },
      };
    });
  };

  const [aiLoading, setAiLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  const autoTranslate = async () => {
    if (!isEdit) {
      toast.error("Save the instrument first, then use AI translation");
      return;
    }
    if (activeLang === "en") return;
    setAiLoading(true);
    try {
      const res = await api.post(`/admin/instruments/${initial.instrument_id}/translate`, {
        target_language: activeLang,
      });
      const t = res.data.translation || {};
      setForm((f) => ({
        ...f,
        translations: { ...(f.translations || {}), [activeLang]: t },
      }));
      toast.success(
        `${res.data.cached ? "Loaded cached" : "Auto-translated"} ${activeLang.toUpperCase()} — please review before saving`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI translation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const translateAll = async () => {
    if (!isEdit) {
      toast.error("Save the instrument first, then use batch AI translation");
      return;
    }
    setBatchLoading(true);
    try {
      const res = await api.post(`/admin/instruments/${initial.instrument_id}/translate-all`, {});
      const map = res.data.translations || {};
      setForm((f) => ({
        ...f,
        translations: { ...(f.translations || {}), ...map },
      }));
      const filled = Object.keys(map).length;
      const cached = (res.data.cached_languages || []).length;
      const errored = Object.keys(res.data.errors || {}).length;
      toast.success(`Filled ${filled} languages${cached ? ` (${cached} cached)` : ""}${errored ? ` · ${errored} failed` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch translation failed");
    } finally {
      setBatchLoading(false);
    }
  };

  const clearLang = (lang) => {
    setForm((f) => {
      const t = { ...(f.translations || {}) };
      delete t[lang];
      return { ...f, translations: t };
    });
  };

  const trLangs = Object.keys(form.translations || {}).filter(
    (l) => form.translations[l] && Object.keys(form.translations[l]).length > 0
  );

  const validate = () => {
    if (!form.instrument_id.trim()) return "instrument_id is required";
    if (!/^[A-Z0-9_]+$/i.test(form.instrument_id)) return "instrument_id can only contain letters, numbers and underscore";
    if (!form.title.trim()) return "Title is required";
    if (form.items.length === 0) return "Add at least one item";
    if (form.items.some((i) => !i.item_id.trim() || !i.text.trim())) return "All items need an item_id and text";
    const ids = form.items.map((i) => i.item_id);
    if (new Set(ids).size !== ids.length) return "item_id values must be unique";
    if (form.response_scale.length < 2) return "Response scale needs at least 2 options";
    if (form.cutoffs.length === 0) return "Add at least one score cutoff";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        instrument_id: form.instrument_id.trim(),
        section: form.section,
        title: form.title.trim(),
        description: form.description,
        scoring: form.scoring,
        enabled: form.enabled,
        response_scale: form.response_scale.map((s) => ({ value: parseInt(s.value, 10), label: s.label })),
        items: form.items.map((i) => ({
          item_id: i.item_id.trim(),
          text: i.text,
          ...(i.reverse ? { reverse: true } : {}),
        })),
        cutoffs: form.cutoffs.map((c) => ({
          max: parseInt(c.max, 10),
          band: c.band,
          label: c.label,
        })),
        cohort_ids: form.cohort_ids,
        site_ids: form.site_ids,
        translations: form.translations || {},
      };
      if (isEdit) {
        const { instrument_id, ...body } = payload;
        await api.put(`/admin/instruments/${initial.instrument_id}`, body);
        toast.success("Instrument updated");
      } else {
        await api.post("/admin/instruments", payload);
        toast.success("Instrument created");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="instrument-builder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit instrument" : "New instrument"}</DialogTitle>
          <DialogDescription>
            Configure the instrument metadata, items, response scale, cutoffs, and cohort/site scope. Scoring runs on numeric values, keeping the model language-neutral.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Meta */}
          <section>
            <h4 className="font-medium mb-3">Metadata</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Instrument ID</Label>
                <Input value={form.instrument_id} disabled={isEdit}
                  onChange={(e) => set("instrument_id", e.target.value.toUpperCase())}
                  placeholder="e.g., AUDIT" data-testid="ib-instrument-id" />
              </div>
              <div>
                <Label>Section</Label>
                <Select value={form.section} onValueChange={(v) => set("section", v)}>
                  <SelectTrigger data-testid="ib-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="Human-friendly title shown to respondents" data-testid="ib-title" />
            </div>
            <div className="mt-3">
              <Label>Description / prompt</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => set("description", e.target.value)}
                data-testid="ib-description" />
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <div>
                <Label>Scoring mode</Label>
                <Select value={form.scoring} onValueChange={(v) => set("scoring", v)}>
                  <SelectTrigger data-testid="ib-scoring"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCORING_MODES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label>Enabled for respondents</Label>
                  <div className="mt-2"><Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="ib-enabled" /></div>
                </div>
              </div>
            </div>
          </section>

          {/* Response scale */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Response scale ({form.response_scale.length} options)</h4>
              <Button size="sm" variant="outline" onClick={addScale} className="rounded-full" data-testid="ib-scale-add">
                <Plus size={12} /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {form.response_scale.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input type="number" value={s.value} className="w-24"
                    onChange={(e) => setListItem("response_scale", idx, { value: e.target.value })}
                    data-testid={`ib-scale-value-${idx}`} />
                  <Input value={s.label}
                    onChange={(e) => setListItem("response_scale", idx, { label: e.target.value })}
                    placeholder="Label (e.g., 'Sometimes')"
                    data-testid={`ib-scale-label-${idx}`} />
                  <button type="button" onClick={() => removeListItem("response_scale", idx)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-2"
                    aria-label="remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Items ({form.items.length})</h4>
              <Button size="sm" variant="outline" onClick={addItem} className="rounded-full" data-testid="ib-item-add">
                <Plus size={12} /> Add item
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx} className="rounded-lg border border-border/60 p-3">
                  <div className="flex items-center gap-2">
                    <GripVertical size={14} className="text-muted-foreground shrink-0" />
                    <Input value={it.item_id} className="w-32"
                      onChange={(e) => setListItem("items", idx, { item_id: e.target.value })}
                      placeholder="Item ID" data-testid={`ib-item-id-${idx}`} />
                    <Input value={it.text}
                      onChange={(e) => setListItem("items", idx, { text: e.target.value })}
                      placeholder="Question text (shown to respondent)"
                      data-testid={`ib-item-text-${idx}`} />
                    <label className="inline-flex items-center gap-2 text-xs whitespace-nowrap">
                      <Switch checked={!!it.reverse} onCheckedChange={(v) => setListItem("items", idx, { reverse: v })}
                        data-testid={`ib-item-reverse-${idx}`} />
                      Reverse
                    </label>
                    <button type="button" onClick={() => removeListItem("items", idx)}
                      className="text-muted-foreground hover:text-destructive transition-colors p-2"
                      aria-label="remove"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Cutoffs */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Score cutoffs (evaluated top-down; first row with score ≤ max wins)</h4>
              <Button size="sm" variant="outline" onClick={addCutoff} className="rounded-full" data-testid="ib-cutoff-add">
                <Plus size={12} /> Add band
              </Button>
            </div>
            <div className="space-y-2">
              {form.cutoffs.map((c, idx) => (
                <div key={idx} className="grid grid-cols-[100px_140px_1fr_40px] gap-2 items-center">
                  <div>
                    <Input type="number" value={c.max}
                      onChange={(e) => setListItem("cutoffs", idx, { max: e.target.value })}
                      data-testid={`ib-cutoff-max-${idx}`} />
                  </div>
                  <Select value={c.band} onValueChange={(v) => setListItem("cutoffs", idx, { band: v })}>
                    <SelectTrigger data-testid={`ib-cutoff-band-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={c.label}
                    onChange={(e) => setListItem("cutoffs", idx, { label: e.target.value })}
                    placeholder="e.g., 'Moderate anxiety'"
                    data-testid={`ib-cutoff-label-${idx}`} />
                  <button type="button" onClick={() => removeListItem("cutoffs", idx)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-2"
                    aria-label="remove"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Scope */}
          <section>
            <h4 className="font-medium mb-2">Scope (leave empty for global)</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cohorts</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {cohorts.length === 0 && <div className="text-xs text-muted-foreground">No cohorts defined</div>}
                  {cohorts.map((c) => {
                    const on = form.cohort_ids.includes(c.cohort_id);
                    return (
                      <button key={c.cohort_id} type="button"
                        onClick={() => toggleScope("cohort_ids", c.cohort_id)}
                        className={"text-xs rounded-full px-3 py-1 border transition-colors " +
                          (on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted")}
                        data-testid={`ib-cohort-${c.name}`}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sites</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {sites.length === 0 && <div className="text-xs text-muted-foreground">No sites defined</div>}
                  {sites.map((s) => {
                    const on = form.site_ids.includes(s.site_id);
                    return (
                      <button key={s.site_id} type="button"
                        onClick={() => toggleScope("site_ids", s.site_id)}
                        className={"text-xs rounded-full px-3 py-1 border transition-colors " +
                          (on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted")}
                        data-testid={`ib-site-${s.name}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {(form.cohort_ids.length > 0 || form.site_ids.length > 0) && (
              <div className="mt-3 text-xs text-muted-foreground">
                Scoped — visible only to respondents in {form.cohort_ids.length} cohort(s) or {form.site_ids.length} site(s).
              </div>
            )}
          </section>

          {/* Translations */}
          <section>
            <div className="mb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="font-medium">Translations</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  {trLangs.length > 0 && trLangs.map((l) => (
                    <Badge key={l} variant="outline" className="text-[10px]" data-testid={`ib-tr-badge-${l}`}>
                      {l.toUpperCase()}
                    </Badge>
                  ))}
                  <Select value={activeLang} onValueChange={setActiveLang}>
                    <SelectTrigger className="w-52 h-8" data-testid="ib-lang-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANG_OPTIONS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={autoTranslate} disabled={aiLoading || batchLoading || !isEdit}
                    className="rounded-full mano-lift" data-testid="ib-ai-translate">
                    <Sparkles size={12} /> {aiLoading ? "Translating…" : "Auto-translate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={translateAll} disabled={aiLoading || batchLoading || !isEdit}
                    className="rounded-full mano-lift" data-testid="ib-ai-translate-all">
                    <Languages size={12} /> {batchLoading ? "Translating all…" : "Translate to all"}
                  </Button>
                  {form.translations?.[activeLang] && Object.keys(form.translations[activeLang]).length > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => clearLang(activeLang)}
                      className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                      data-testid="ib-tr-clear">
                      <Trash2 size={12} /> Clear {activeLang.toUpperCase()}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Scoring uses numeric values only — translations are display-only. Respondents see fields in their preferred language; blanks fall back to English.
              </p>
            </div>

            <div className="rounded-lg border border-border/60 p-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Title ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={form.translations?.[activeLang]?.title || ""}
                    onChange={(e) => setTrField(activeLang, "title", e.target.value)}
                    placeholder={form.title}
                    data-testid="ib-tr-title" />
                </div>
                <div>
                  <Label className="text-xs">Description ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={form.translations?.[activeLang]?.description || ""}
                    onChange={(e) => setTrField(activeLang, "description", e.target.value)}
                    placeholder={form.description || "…"}
                    data-testid="ib-tr-description" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Item text</Label>
                <div className="mt-2 space-y-1.5">
                  {form.items.map((it) => (
                    <div key={it.item_id} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] w-24 justify-center shrink-0 font-mono">{it.item_id}</Badge>
                      <Input
                        value={form.translations?.[activeLang]?.items?.[it.item_id] || ""}
                        onChange={(e) => setTrNested(activeLang, "items", it.item_id, e.target.value)}
                        placeholder={it.text || "(untranslated)"}
                        data-testid={`ib-tr-item-${it.item_id}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Response scale labels</Label>
                  <div className="mt-2 space-y-1.5">
                    {form.response_scale.map((s) => (
                      <div key={s.value} className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] w-14 justify-center shrink-0 font-mono">{s.value}</Badge>
                        <Input
                          value={form.translations?.[activeLang]?.response_scale?.[String(s.value)] || ""}
                          onChange={(e) => setTrNested(activeLang, "response_scale", String(s.value), e.target.value)}
                          placeholder={s.label}
                          data-testid={`ib-tr-scale-${s.value}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Cutoff band labels</Label>
                  <div className="mt-2 space-y-1.5">
                    {form.cutoffs.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] w-14 justify-center shrink-0 font-mono">{idx}</Badge>
                        <Input
                          value={form.translations?.[activeLang]?.cutoffs?.[String(idx)] || ""}
                          onChange={(e) => setTrNested(activeLang, "cutoffs", String(idx), e.target.value)}
                          placeholder={c.label}
                          data-testid={`ib-tr-cutoff-${idx}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="ib-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full mano-lift" data-testid="ib-save">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create instrument"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
